import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef } from "react";
import type { RefObject } from "react";

import type { OpportunityHubGroup } from "./hub-chrome-grouping.js";
import {
  buildOpportunityVirtualItems,
  groupKeysToLoad,
  groupKeysToPage,
  shouldLoadMoreGroups,
  visibleGroupKeysFromItems,
  type HubOpportunityVirtualItem,
} from "./hub-opportunity-virtual-items.js";

const ROW_ESTIMATE_PX = 64;

function estimateRowSize(): number {
  return ROW_ESTIMATE_PX;
}
const VIRTUAL_OVERSCAN = 8;
/** Before the scroller is measured, only a short prefix may request children. */
const UNMEASURED_WINDOW = 15;

/**
 * Flat group/row/skeleton window. The next page is requested only after layout,
 * and only for the list that is actually on screen.
 */
export function useOpportunityVirtualWindow({
  groups,
  expandedGroupKeys,
  loadingKeys,
  hasMoreByKey,
  isLoadingMoreGroups,
  hasMoreGroups,
  scrollRef,
  onLoadMoreGroups,
  onLoadMoreGroup,
  onGroupsNeeded,
  groupLoadGeneration = 0,
  cardsPerLine,
  groupFooters = false,
  estimateItemSize = estimateRowSize,
}: {
  groups: readonly OpportunityHubGroup[];
  expandedGroupKeys: ReadonlySet<string>;
  loadingKeys: ReadonlySet<string>;
  hasMoreByKey: Readonly<Record<string, boolean>>;
  isLoadingMoreGroups: boolean;
  hasMoreGroups: boolean;
  scrollRef: RefObject<HTMLElement | null>;
  onLoadMoreGroups?: () => void;
  onLoadMoreGroup?: (groupKey: string) => void;
  onGroupsNeeded?: (groupKeys: readonly string[]) => void;
  /** Bumped after a scope reset so this effect asks for rows again. */
  groupLoadGeneration?: number;
  /** Packs rows into card grid lines; omit for one row per item. */
  cardsPerLine?: number;
  /** Ends each loaded group in a "Load more" footer; group pages are then never requested on scroll. */
  groupFooters?: boolean;
  estimateItemSize?: (item: HubOpportunityVirtualItem) => number;
}) {
  const items = useMemo(
    () =>
      buildOpportunityVirtualItems({
        groups,
        expandedGroupKeys,
        loadingKeys,
        hasMoreByKey,
        isLoadingMoreGroups,
        cardsPerLine,
        groupFooters,
      }),
    [
      groups,
      expandedGroupKeys,
      loadingKeys,
      hasMoreByKey,
      isLoadingMoreGroups,
      cardsPerLine,
      groupFooters,
    ],
  );
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (items[index] ? estimateItemSize(items[index]) : ROW_ESTIMATE_PX),
    getItemKey: (index) => items[index]?.key ?? index,
    overscan: VIRTUAL_OVERSCAN,
    measureElement: (element) => element.getBoundingClientRect().height,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const measured = virtualItems.length > 0;
  const visibleIndexes = measured
    ? virtualItems.map((item) => item.index)
    : items.slice(0, UNMEASURED_WINDOW).map((_, index) => index);
  const visibleIndexKey = visibleIndexes.join(",");
  const onGroupsNeededRef = useRef(onGroupsNeeded);
  const onLoadMoreGroupRef = useRef(onLoadMoreGroup);
  const onLoadMoreGroupsRef = useRef(onLoadMoreGroups);
  onGroupsNeededRef.current = onGroupsNeeded;
  onLoadMoreGroupRef.current = onLoadMoreGroup;
  onLoadMoreGroupsRef.current = onLoadMoreGroups;

  useEffect(() => {
    const visible = new Set(visibleGroupKeysFromItems(items, visibleIndexes));
    onGroupsNeededRef.current?.(groupKeysToLoad(groups, visible, expandedGroupKeys));
    if (!measured) return;
    for (const key of groupKeysToPage(items, visibleIndexes, hasMoreByKey, loadingKeys)) {
      onLoadMoreGroupRef.current?.(key);
    }
    if (shouldLoadMoreGroups(groups, visible, hasMoreGroups, isLoadingMoreGroups)) {
      onLoadMoreGroupsRef.current?.();
    }
  }, [
    items,
    visibleIndexKey,
    measured,
    groups,
    expandedGroupKeys,
    hasMoreByKey,
    loadingKeys,
    hasMoreGroups,
    isLoadingMoreGroups,
    groupLoadGeneration,
  ]);

  const topPad = measured ? (virtualItems[0]?.start ?? 0) : 0;
  const bottomPad =
    measured && virtualItems.length > 0
      ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1]!.end
      : 0;

  return {
    items,
    rendered: measured ? virtualItems.map((item) => item.index) : visibleIndexes,
    topPad,
    bottomPad,
    measureElement: virtualizer.measureElement,
  };
}
